import { ExtractionError, type DecoderRule, type Value } from "../types.ts";
import { compartiCatalog, compartiDetail, compartiChapters } from './CompartiComics.ts';

export type DecoderHandler = (input: string) => Value;
// These handlers are shipped with Yomiri. Profiles cannot register executable code.
const handlers: Readonly<Record<string, DecoderHandler>> = Object.freeze({
  'comparticomics-catalog': compartiCatalog,
  'comparticomics-detail': compartiDetail,
  'comparticomics-chapters': compartiChapters,
  "themesia-pages": (input) => {
    const reader = handlers["themesia-reader-json"](input);
    if (
      !reader ||
      Array.isArray(reader) ||
      typeof reader !== "object" ||
      reader.protected ||
      reader.is_novel
    )
      throw new Error("El capítulo requiere acceso especial o no es manga.");
    const sources = reader.sources;
    if (!Array.isArray(sources)) throw new Error("No hay servidores de páginas.");
    for (const source of sources)
      if (
        source &&
        typeof source === "object" &&
        !Array.isArray(source) &&
        Array.isArray(source.images) &&
        source.images.length &&
        source.images.every((url) => typeof url === "string")
      )
        return source.images;
    throw new Error("No hay páginas en los servidores del lector.");
  },
  "leercapitulo-pages": (input) => {
    const document = new DOMParser().parseFromString(input, "text/html");
    const urls = [...document.querySelectorAll("#lcPages img, .lc-pages img")]
      .map((img) =>
        ["data-src", "data-lazy-src", "data-cfsrc", "src"]
          .map((key) => img.getAttribute(key)?.trim())
          .find(Boolean),
      )
      .filter((value): value is string => !!value);
    if (urls.length) return urls;
    // Historical readers sometimes store a JSON array, optionally wrapped in atob("...").
    for (const script of document.querySelectorAll("script")) {
      const text = script.textContent ?? "";
      const encoded = text.match(
        /(?:lcPages|readerPages|pages)\s*=\s*(?:JSON\.parse\(\s*)?atob\(\s*("(?:[^"\\]|\\.)*")\s*\)/,
      );
      const literal = text.match(/(?:lcPages|readerPages|pages)\s*=\s*(\[[\s\S]*?\])\s*[;,]/);
      if (!encoded && !literal) continue;
      const array: unknown = JSON.parse(
        encoded ? atob(JSON.parse(encoded[1]) as string) : literal![1],
      );
      if (!Array.isArray(array)) throw new Error("El lector no contiene una lista de páginas.");
      return array.map((item) => {
        const url = typeof item === "string" ? item : (item?.url ?? item?.page_image);
        if (typeof url !== "string") throw new Error("Página inválida.");
        return url;
      });
    }
    throw new Error("No se encontraron páginas de LeerCapitulo en DOM ni JSON.");
  },
  json: (input) => JSON.parse(input) as Value,
  "base64-json": (input) =>
    JSON.parse(
      new TextDecoder().decode(Uint8Array.from(atob(input.trim()), (c) => c.charCodeAt(0))),
    ) as Value,
  "themesia-reader-json": (input) => {
    const marker = input.indexOf("ts_reader.run(");
    if (marker < 0) throw new Error("No se encontró ts_reader.run.");
    const start = input.indexOf("{", marker);
    if (start < 0) throw new Error("No se encontró un objeto JSON.");
    let depth = 0,
      quoted = false,
      escaped = false;
    for (let i = start; i < input.length; i++) {
      const char = input[i];
      if (quoted) {
        if (escaped) escaped = false;
        else if (char === "\\") escaped = true;
        else if (char === '"') quoted = false;
      } else if (char === '"') quoted = true;
      else if (char === "{") depth++;
      else if (char === "}" && --depth === 0) return JSON.parse(input.slice(start, i + 1)) as Value;
    }
    throw new Error("JSON incompleto.");
  },
});
export function hasDecoder(rule: DecoderRule): boolean {
  return rule.kind === "builtin" && Object.hasOwn(handlers, rule.name);
}
export function decode(rule: DecoderRule, input: string): Value {
  try {
    if (!hasDecoder(rule)) throw new Error(`Decoder desconocido: ${rule.name}`);
    return handlers[rule.name](input);
  } catch (error) {
    throw new ExtractionError(
      "DECODER_ERROR",
      error instanceof Error ? error.message : "Falló el decoder.",
    );
  }
}
